<template>
  <div
    class="position-subscription-history-row"
    :class="`position-subscription-history-row--${rowState}`"
  >
    <div class="position-subscription-history-row__header">
      <p class="position-subscription-history-row__title">{{ titleText }}</p>
      <div class="position-subscription-history-row__window">
        <p v-if="windowText" class="position-subscription-history-row__window-text">
          {{ windowText }}
        </p>
        <button
          v-if="showUnsubscribe"
          type="button"
          class="position-subscription-history-row__unsubscribe"
          aria-label="Unsubscribe"
          @click="onUnsubscribe"
        >
          ×
        </button>
      </div>
    </div>

    <div v-if="detailRows.length" class="position-subscription-history-row__versions">
      <div
        v-for="row in detailRows"
        :key="row.key"
        class="position-subscription-history-row__version"
        :class="rowRowClass(row)"
      >
        <span
          class="position-subscription-history-row__version-label"
          :class="{
            'position-subscription-history-row__version-label--gap': row.kind === 'gap',
          }"
        >
          {{ row.label }}
        </span>
        <span
          v-if="row.payBand"
          class="position-subscription-history-row__version-amount"
        >
          {{ row.payBand }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import {
  canUnsubscribe,
  deriveSubscriptionState,
  formatHistoryTitle,
  formatHistoryWindow,
  mapHistoryGap,
  mapHistoryVersion,
  versionOrdinalValue,
} from "./positionSubscriptionHistoryRowUtils";

const props = defineProps({
  subscriptionId: {
    type: [String, Number],
    default: null,
  },
  subscriptionCode: {
    type: String,
    required: true,
  },
  positionTitle: {
    type: String,
    default: "",
  },
  effectiveWindow: {
    type: Object,
    default: () => ({ start: null, end: null }),
  },
  versions: {
    type: Array,
    default: () => [],
  },
  coverageGaps: {
    type: Array,
    default: () => [],
  },
  today: {
    type: [String, Date],
    default: null,
  },
});

const emit = defineEmits(["unsubscribe"]);

const rowState = computed(() =>
  deriveSubscriptionState(props.effectiveWindow.start, props.effectiveWindow.end, props.today)
);

const titleText = computed(() =>
  formatHistoryTitle(props.subscriptionCode, props.positionTitle)
);

const windowText = computed(() =>
  formatHistoryWindow(props.effectiveWindow.start, props.effectiveWindow.end)
);

const showUnsubscribe = computed(() => canUnsubscribe(rowState.value));

const versionRows = computed(() =>
  [...props.versions]
    .map((version) => mapHistoryVersion(version, props.today))
    .sort((a, b) => versionOrdinalValue(b.ordinal) - versionOrdinalValue(a.ordinal))
);

const detailRows = computed(() => [
  ...versionRows.value,
  ...(props.coverageGaps || []).map((gap) => mapHistoryGap(gap)),
]);

function rowRowClass(row) {
  if (row.kind === "gap") {
    return "position-subscription-history-row__version--gap";
  }
  return `position-subscription-history-row__version--${row.state}`;
}

function onUnsubscribe() {
  emit("unsubscribe", props.subscriptionId);
}
</script>

<style scoped>
.position-subscription-history-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
  box-sizing: border-box;
  width: 100%;
  padding: 10px 14px;
  border-radius: 8px;
  border-style: solid;
  overflow: hidden;
}

.position-subscription-history-row--lapsed {
  background: #e3e7ec;
  border-width: 1.5px;
  border-color: #9ea4b0;
}

.position-subscription-history-row--current-open,
.position-subscription-history-row--current-closed {
  background: #e8fbf0;
  border-width: 1.5px;
  border-color: #027a48;
}

.position-subscription-history-row--future-open,
.position-subscription-history-row--future-closed {
  background: #f0f9ff;
  border-width: 1px;
  border-color: #eaecf0;
}

.position-subscription-history-row__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  min-width: 0;
}

.position-subscription-history-row__title,
.position-subscription-history-row__window-text,
.position-subscription-history-row__version-label,
.position-subscription-history-row__version-amount {
  margin: 0;
  font-family: Inter, sans-serif;
  font-style: normal;
  line-height: normal;
  white-space: nowrap;
}

.position-subscription-history-row__title {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 13px;
  font-weight: 500;
}

.position-subscription-history-row--lapsed .position-subscription-history-row__title,
.position-subscription-history-row--lapsed .position-subscription-history-row__window-text {
  color: #4a5463;
}

.position-subscription-history-row--current-open .position-subscription-history-row__title,
.position-subscription-history-row--current-closed .position-subscription-history-row__title,
.position-subscription-history-row--current-open .position-subscription-history-row__window-text,
.position-subscription-history-row--current-closed .position-subscription-history-row__window-text,
.position-subscription-history-row--current-open .position-subscription-history-row__version-label,
.position-subscription-history-row--current-closed .position-subscription-history-row__version-label,
.position-subscription-history-row--current-closed .position-subscription-history-row__version-amount {
  color: #027a48;
}

.position-subscription-history-row--future-open .position-subscription-history-row__title,
.position-subscription-history-row--future-closed .position-subscription-history-row__title,
.position-subscription-history-row--future-open .position-subscription-history-row__window-text,
.position-subscription-history-row--future-closed .position-subscription-history-row__window-text {
  color: #101828;
}

.position-subscription-history-row__window {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.position-subscription-history-row__window-text {
  font-size: 12px;
  font-weight: 400;
}

.position-subscription-history-row__unsubscribe {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 1px solid #eaecf0;
  border-radius: 6px;
  background: #fff;
  color: #667085;
  font-family: Inter, sans-serif;
  font-size: 13px;
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
}

.position-subscription-history-row__versions {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.position-subscription-history-row__version {
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  width: 100%;
  min-height: 13px;
  padding: 0 0 0 18px;
}

.position-subscription-history-row__version-label,
.position-subscription-history-row__version-amount {
  font-size: 11px;
  font-weight: 400;
  color: #667085;
}

.position-subscription-history-row--current-open
  .position-subscription-history-row__version--lapsed {
  margin-left: -14px;
  margin-right: -14px;
  padding: 0 14px 0 32px;
  background: #e3e7ec;
}

.position-subscription-history-row--current-open
  .position-subscription-history-row__version--lapsed
  .position-subscription-history-row__version-label,
.position-subscription-history-row--current-open
  .position-subscription-history-row__version:not(
    .position-subscription-history-row__version--lapsed
  ):not(.position-subscription-history-row__version--gap)
  .position-subscription-history-row__version-label {
  color: #027a48;
}

.position-subscription-history-row__version-label--gap,
.position-subscription-history-row__version--gap
  .position-subscription-history-row__version-label,
.position-subscription-history-row--current-open
  .position-subscription-history-row__version--gap
  .position-subscription-history-row__version-label {
  color: #b42318;
}
</style>
